/**
 * TenantThemeProvider · injects Sovereign Theme tokens as CSS custom
 * properties on the document root so every component automatically
 * adopts the per-subdomain palette without an additional render pass.
 *
 * Director directive 2026.02 · Strike #6 (Sovereign Theme Bridge).
 *
 * On mount:
 *   1. GET /api/tenant/branding (Host header is sent automatically by the browser)
 *   2. Inject `--brand-*` CSS variables on documentElement
 *   3. Stash the resolved tenant in context for components that need
 *      raw values (icon name, display name, tagline, CTA copy)
 *
 * Failure mode: if the API call fails we fall back to the existing CSS
 * defaults — no visible regression, just no per-vertical theming.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const TenantThemeContext = createContext(null);

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function applyThemeToRoot(theme) {
    if (!theme || typeof document === "undefined") return;
    const root = document.documentElement;
    const map = {
        "--brand-primary":  theme.primary,
        "--brand-accent":   theme.accent,
        "--brand-bg":       theme.bg,
        "--brand-surface":  theme.surface,
        "--brand-text":     theme.text,
        "--brand-border":   theme.border,
        "--brand-gradient": theme.gradient,
        "--brand-glow":     theme.glow,
    };
    Object.entries(map).forEach(([k, v]) => {
        if (v) root.style.setProperty(k, v);
    });
}

export function TenantThemeProvider({ children }) {
    const [tenant, setTenant] = useState(null);
    const [resolvedVia, setResolvedVia] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/tenant/branding`, {
                    withCredentials: false,
                    timeout: 6000,
                });
                if (cancelled) return;
                if (data?.tenant) {
                    setTenant(data.tenant);
                    setResolvedVia(data.resolved_via || null);
                    applyThemeToRoot(data.tenant.theme);
                    if (data.tenant.display_name) {
                        document.title = data.tenant.display_name;
                    }
                }
            } catch (_e) {
                // silent fallback to CSS defaults
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <TenantThemeContext.Provider value={{ tenant, resolvedVia }}>
            {children}
        </TenantThemeContext.Provider>
    );
}

export function useTenantTheme() {
    return useContext(TenantThemeContext) || { tenant: null, resolvedVia: null };
}
